// -----------------------------------------------------------
// FloraIQ - TreeMetrics3DTable — Aba Métricas por Árvore
// -----------------------------------------------------------

import { useState, useMemo } from "react";
import type { TreeMetric3DRow } from "@/lib/mappers";
import { NoRealData } from "@/components/shared";
import { TREE_METRIC_3D_STATUS_LABELS } from "@/config/qualidade-3d-config";
import { ConfidenceBadge } from "./PointCloudStatusBadge";

interface TreeMetrics3DTableProps {
  items: TreeMetric3DRow[];
  loading: boolean;
  error: string | null;
}

const m = (v: number | null, unit: string) => (v !== null ? `${v} ${unit}` : "—");
const projectLabel = (id: string) => (id ? `Projeto #${id}` : "—");

export default function TreeMetrics3DTable({ items: treeMetrics3D, loading, error }: TreeMetrics3DTableProps) {
  const [filterProjeto, setFilterProjeto] = useState("");
  const [filterConfianca, setFilterConfianca] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showDivergencias, setShowDivergencias] = useState(false);

  const projetos = useMemo(() => [...new Set(treeMetrics3D.map((t) => t.projeto))], [treeMetrics3D]);

  const filtered = useMemo(() => {
    return treeMetrics3D.filter((t) => {
      if (filterProjeto && t.projeto !== filterProjeto) return false;
      if (filterConfianca && t.confianca !== filterConfianca) return false;
      if (filterStatus === "divergente" && t.status !== "divergente") return false;
      if (filterStatus === "sem_3d" && t.status !== "sem_3d") return false;
      if (filterStatus === "validado" && t.status !== "validado") return false;
      return true;
    });
  }, [filterProjeto, filterConfianca, filterStatus, treeMetrics3D]);

  const divergentes = useMemo(() => treeMetrics3D.filter((t) => t.status === "divergente"), [treeMetrics3D]);

  const hasFilters = filterProjeto || filterConfianca || filterStatus;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-foreground-500 py-10 justify-center">
        <i className="ri-loader-4-line animate-spin"></i> Carregando métricas 3D...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50/60 border border-red-200/60 rounded-card p-4 text-xs text-red-700">
        Não foi possível carregar as métricas 3D: {error}
      </div>
    );
  }

  if (treeMetrics3D.length === 0) {
    return (
      <NoRealData
        title="Nenhuma métrica 3D registrada"
        icon="ri-stack-line"
        description="Ainda não há exemplares arbóreos com métricas extraídas de nuvem de pontos para comparar com a medição de campo."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={filterProjeto} onChange={(e) => setFilterProjeto(e.target.value)} className="px-3 py-2 text-sm border border-background-200/70 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">Todos os projetos</option>
          {projetos.map((p) => <option key={p} value={p}>{projectLabel(p)}</option>)}
        </select>
        <select value={filterConfianca} onChange={(e) => setFilterConfianca(e.target.value)} className="px-3 py-2 text-sm border border-background-200/70 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">Todas as confianças</option>
          <option value="alta">Alta</option>
          <option value="media">Média</option>
          <option value="baixa">Baixa</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-background-200/70 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">Todos os status</option>
          <option value="validado">Validado</option>
          <option value="divergente">Divergente</option>
          <option value="sem_3d">Sem métrica 3D</option>
        </select>
        <button
          onClick={() => setShowDivergencias(!showDivergencias)}
          className={`text-xs px-3 py-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${showDivergencias ? "bg-orange-50 text-orange-700 border-orange-300" : "bg-white text-foreground-600 border-background-200/70 hover:bg-background-50"}`}
        >
          <i className="ri-contrast-2-line mr-1"></i>
          Divergências Campo x 3D
          {divergentes.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">{divergentes.length}</span>
          )}
        </button>
        {hasFilters && (
          <button onClick={() => { setFilterProjeto(""); setFilterConfianca(""); setFilterStatus(""); }} className="text-xs text-primary-500 hover:text-primary-600 cursor-pointer px-2 py-2 whitespace-nowrap">
            <i className="ri-close-line mr-1"></i>Limpar
          </button>
        )}
      </div>

      {/* Painel de divergências */}
      {showDivergencias && (
        <div className="bg-orange-50/60 border border-orange-200/60 rounded-card p-4 slide-up">
          <h4 className="text-sm font-bold text-foreground-900 font-heading mb-3">Divergências Campo x 3D</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {divergentes.map((d) => (
              <div key={d.codigoCta} className="bg-white rounded-md border border-orange-200/50 p-2.5 flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line text-orange-600 text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-foreground-900 font-heading">{d.codigoCta}</p>
                  <p className="text-xs text-foreground-500">{d.especie}</p>
                  <p className="text-xs text-orange-600 mt-1">
                    Altura: {m(d.alturaCampo, "m")} (campo) vs {m(d.altura3D, "m")} (3D)
                    {d.inclinacao !== null && ` | Inclinação: ${d.inclinacao}°`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabela */}
      <p className="text-xs text-foreground-500">{filtered.length} exemplares na comparação campo x 3D</p>
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-50/50">
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Código CTA</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Espécie</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Projeto</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Alt. Campo</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Alt. 3D</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Diferença</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Copa 3D</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Vol. Copa</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Incl.</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Confiança</th>
                <th className="text-left px-3 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const difAltura = t.altura3D !== null && t.alturaCampo !== null ? `${(t.altura3D - t.alturaCampo).toFixed(1)} m` : "—";
                const difClass = t.altura3D !== null && t.alturaCampo !== null && Math.abs(t.altura3D - t.alturaCampo) > 1.0 ? "text-orange-600 font-medium" : "text-foreground-600";
                return (
                  <tr key={t.codigoCta} className="border-b border-background-100/50 hover:bg-background-50/30 transition-colors">
                    <td className="px-3 py-2 text-xs font-medium text-foreground-800 whitespace-nowrap font-mono">{t.codigoCta}</td>
                    <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{t.especie || "—"}</td>
                    <td className="px-3 py-2 text-xs text-foreground-500 whitespace-nowrap">{projectLabel(t.projeto)}</td>
                    <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{m(t.alturaCampo, "m")}</td>
                    <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{m(t.altura3D, "m")}</td>
                    <td className={`px-3 py-2 text-xs whitespace-nowrap ${difClass}`}>{difAltura}</td>
                    <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{m(t.diametroCopa3D, "m")}</td>
                    <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{m(t.volumeCopa, "m³")}</td>
                    <td className="px-3 py-2 text-xs text-foreground-600 whitespace-nowrap">{t.inclinacao !== null ? `${t.inclinacao}°` : "—"}</td>
                    <td className="px-3 py-2"><ConfidenceBadge level={t.confianca} /></td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full border whitespace-nowrap ${
                        t.status === "validado" ? "bg-green-50 text-green-700 border-green-200" :
                        t.status === "divergente" ? "bg-orange-50 text-orange-700 border-orange-200" :
                        "bg-gray-50 text-gray-600 border-gray-200"
                      }`}>
                        {TREE_METRIC_3D_STATUS_LABELS[t.status] ?? t.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}